/*
 * Expiry Date Fix — fixing group (direct URL only, no menu entry).
 *
 * Lets an operator find a center_stock_in_details row by drug UID (code_no) and/or
 * batch number, and correct ONLY its expire_date — never touches any quantity field.
 * See docs/dev-tools.md (backend repo) for why this exists and what it's for.
 */
import { useState } from 'react';
import { Alert, Badge, Button, Card, Col, Form, Row, Table } from 'react-bootstrap';
import Swal from 'sweetalert2';
import ToastifyService from '../../services/ToastifyService';
import { useExpiryFix } from './ContextAPI';

export default function Index() {
    const { rows, loading, searched, errMsg, search, updateExpiryDate } = useExpiryFix();
    const [codeNo, setCodeNo] = useState('');
    const [batchNo, setBatchNo] = useState('');
    const [newDates, setNewDates] = useState({}); // { [rowId]: 'YYYY-MM-DD' }
    const [savingId, setSavingId] = useState(null);
    const [localRows, setLocalRows] = useState(null); // lets us patch a row after a successful update without re-searching

    const displayRows = localRows || rows;

    const handleSearch = (e) => {
        e.preventDefault();
        if (!codeNo.trim() && !batchNo.trim()) {
            ToastifyService.dbErrorNotify('Enter at least a UID or a Batch Number.');
            return;
        }
        setLocalRows(null);
        search(codeNo.trim(), batchNo.trim());
    };

    const handleDateChange = (id, value) => {
        setNewDates((prev) => ({ ...prev, [id]: value }));
    };

    const handleUpdate = async (row) => {
        const chosen = newDates[row.id];
        if (!chosen) {
            ToastifyService.dbErrorNotify('Pick a new expiry date first.');
            return;
        }
        if (chosen === row.expire_date) {
            ToastifyService.dbErrorNotify('New date is the same as the current one.');
            return;
        }

        const today = new Date().toISOString().slice(0, 10);
        const warnings = [];
        warnings.push('This changes <b>only the expiry date</b>. Invoice/Receive/Dispatch/Available quantities are never touched by this tool.');
        if (row.already_dispatched) {
            warnings.push(
                `This batch has already been <b>dispatched</b> (Dispatch Qty: ${row.dispatch_qty}). It is likely also present at Partner/Facility/Dispensary level with their own copy of the expiry date — <b>this tool does not update those</b>; they would need a separate fix if required.`
            );
        }
        if (chosen < today) {
            warnings.push('⚠ The new date is <b>in the past</b> — this batch will immediately show as <b>EXPIRED</b> in reports.');
        }
        if (chosen < row.expire_date) {
            warnings.push('⚠ You are moving the expiry date <b>earlier</b> than it currently is — currently-active or near-expiry reports for this batch may change status.');
        }
        warnings.push('Double-check this against your source document (e.g. the reconciliation sheet) before confirming.');

        const result = await Swal.fire({
            title: 'Confirm expiry date change?',
            html: `
        <div style="text-align:left">
          <p><b>${row.code_no}</b> ${row.drug_name} — batch <b>${row.batch_no}</b></p>
          <p>Current expiry: <b>${row.expire_date}</b> &rarr; New expiry: <b>${chosen}</b></p>
          <hr/>
          <ul style="padding-left:18px">${warnings.map((w) => `<li style="margin-bottom:6px">${w}</li>`).join('')}</ul>
        </div>`,
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, update expiry date',
            cancelButtonText: 'Cancel',
            confirmButtonColor: '#d9534f',
            reverseButtons: true,
            width: 600,
        });

        if (!result.isConfirmed) return;

        setSavingId(row.id);
        try {
            const data = await updateExpiryDate(row.id, chosen);
            if (data?.status === 'success') {
                ToastifyService.updateNotify('Expiry date updated.');
                setLocalRows((prevLocal) => {
                    const base = prevLocal || rows;
                    return base.map((r) => (r.id === row.id ? { ...r, expire_date: chosen } : r));
                });
            } else {
                ToastifyService.errorNotify(data?.msg || 'Update failed.');
            }
        } catch (err) {
            ToastifyService.errorNotify(err?.response?.data?.msg || 'Update failed — server not responding.');
        } finally {
            setSavingId(null);
        }
    };

    return (
        <Card>
            <Card.Header className="cardHeader">
                <h3>
                    Expiry Date Fix{' '}
                    <small className="text-muted" style={{ fontSize: '14px' }}>
                        (fixing group — Center warehouse only)
                    </small>
                </h3>
            </Card.Header>
            <Card.Body style={{ backgroundColor: '#dadada', padding: '20px', borderRadius: '0 0 8px 8px' }}>
                <Alert variant="warning" className="mb-3">
                    <b>Scope &amp; risk:</b> this tool edits <code>expire_date</code> on a single{' '}
                    <b>Center warehouse</b> stock-in row only — it never changes any quantity. It does{' '}
                    <b>not</b> cascade the correction to Partner/Facility/Dispensary copies of the same
                    batch if that stock has already moved down the chain. Always confirm you picked the
                    exact right row (same PO No / GRN No / quantities) when a batch number has more than
                    one matching row below.
                </Alert>

                <Form onSubmit={handleSearch} className="mb-3" style={{ background: '#fff', padding: '14px', borderRadius: '6px' }}>
                    <Row className="align-items-end">
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label>UID (Drug code_no)</Form.Label>
                                <Form.Control
                                    value={codeNo}
                                    onChange={(e) => setCodeNo(e.target.value)}
                                    placeholder="e.g. ESS084"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Form.Group>
                                <Form.Label>Batch Number (partial match ok)</Form.Label>
                                <Form.Control
                                    value={batchNo}
                                    onChange={(e) => setBatchNo(e.target.value)}
                                    placeholder="e.g. 06823C3"
                                />
                            </Form.Group>
                        </Col>
                        <Col md={4}>
                            <Button type="submit" variant="primary" disabled={loading}>
                                {loading ? 'Searching...' : 'Search'}
                            </Button>
                        </Col>
                    </Row>
                </Form>

                {errMsg && <Alert variant="danger">{errMsg}</Alert>}

                {searched && !loading && displayRows.length === 0 && !errMsg && (
                    <Alert variant="secondary">No matching rows found.</Alert>
                )}

                {displayRows.length > 0 && (
                    <div style={{ background: '#fff', borderRadius: '6px', padding: '10px', overflowX: 'auto' }}>
                        <Table bordered hover responsive size="sm" className="mb-0">
                            <thead>
                                <tr>
                                    <th>UID</th>
                                    <th>Drug</th>
                                    <th>Batch No</th>
                                    <th>PO / GRN</th>
                                    <th>Manufacturer</th>
                                    <th>Mfg Date</th>
                                    <th>Current Expiry</th>
                                    <th>Receive / Dispatch / Available</th>
                                    <th style={{ minWidth: 170 }}>New Expiry</th>
                                    <th />
                                </tr>
                            </thead>
                            <tbody>
                                {displayRows.map((row) => (
                                    <tr key={row.id}>
                                        <td>{row.code_no}</td>
                                        <td>{row.drug_name}</td>
                                        <td>
                                            {row.batch_no}{' '}
                                            {row.already_dispatched && (
                                                <Badge bg="warning" text="dark" title="dispatch_qty > 0">
                                                    dispatched
                                                </Badge>
                                            )}
                                        </td>
                                        <td>
                                            {row.po_no}
                                            <br />
                                            <small className="text-muted">{row.grn_no}</small>
                                        </td>
                                        <td>{row.manufacturer}</td>
                                        <td>{row.mfg_date || '-'}</td>
                                        <td>
                                            <b>{row.expire_date}</b>
                                        </td>
                                        <td>
                                            {row.receive_qty} / {row.dispatch_qty} / {row.available_qty}
                                        </td>
                                        <td>
                                            <Form.Control
                                                type="date"
                                                value={newDates[row.id] || ''}
                                                onChange={(e) => handleDateChange(row.id, e.target.value)}
                                            />
                                        </td>
                                        <td>
                                            <Button
                                                size="sm"
                                                variant="success"
                                                disabled={savingId === row.id}
                                                onClick={() => handleUpdate(row)}
                                            >
                                                {savingId === row.id ? 'Saving...' : 'Update'}
                                            </Button>
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </Table>
                    </div>
                )}
            </Card.Body>
        </Card>
    );
}
